import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Typography,
} from "../material-old";
import { Button as AntButton, message, Table as AntTable } from "antd";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import {
  BASE_REQUIREMENTS_TABLE_HEAD,
  RecordRow,
} from "./fixtures/baseRequirementsTable.tsx";
import { useNavigate } from "react-router-dom";
import { fetchAllBaseRequirements } from "../../redux/actions/baseRequirementsActions.tsx";
import { BaseRequirement } from "../../redux/interfaces/base-requirements.types.ts";
import { generateTagColors } from "../../utils/common/generateTagColors.ts";
import { CoreLoading } from "../common/CoreLoading.tsx";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";

export const TableComponent = () => {
  const [page, setPage] = useState(1);
  const { text: search } = useAppSelector((state) => state.search);
  const [tagColors, setTagColors] = useState({});

  const limit = 5;
  const {
    results: requirements,
    next,
    previous,
    count,
    loading,
  } = useAppSelector((state) => state.baseRequirements);
  const [dataSource, setDataSource] = useState<RecordRow[]>([]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const totalPages = Math.ceil(count / limit);

  useEffect(() => {
    fetchInitialBaseRequirements();
  }, []);

  useEffect(() => {
    onFilterBaseRequirements();
  }, [search]);

  useEffect(() => {
    onUpdateSource();
  }, [requirements]);

  const onFilterBaseRequirements = () => {
    if (!search) {
      onUpdateDataSource(tagColors, requirements);
      return;
    }

    const filtered = requirements.filter(
      (requirement) =>
        requirement.name.toLowerCase().includes(search.toLowerCase()) ||
        requirement.type.toLowerCase().includes(search.toLowerCase()),
    );
    onUpdateDataSource(tagColors, filtered);
  };

  const onUpdateDataSource = (
    tags: { [key: string]: string },
    requirements: BaseRequirement[],
  ) => {
    const data = requirements.map((req) => ({
      ...req,
      name: req.name,
      key: req.id,
      color: tags[`${req.type}`],
      onClick: () => navigate(`/base-requirements/${req.id}`),
    }));
    setDataSource(data);
  };

  const onUpdateSource = () => {
    if (!requirements.length) return;

    const tags = generateTagColors(requirements.map((req) => `${req.type}`));
    setTagColors(tags);

    onUpdateDataSource(tags, requirements);
  };

  const fetchInitialBaseRequirements = async () => {
    if (loading) return;

    try {
      await dispatch(
        fetchAllBaseRequirements(`/docvault/base-requirements/?limit=${limit}`),
      ).unwrap();
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudieron cargar los requisitos."));
    }
  };

  const onFetchPrevious = async () => {
    const reqs = await dispatch(fetchAllBaseRequirements(previous!)).unwrap();
    setPage(page - 1);

    onUpdateDataSource(tagColors, reqs.results);
  };

  const onFetchNext = async () => {
    const reqs = await dispatch(fetchAllBaseRequirements(next!)).unwrap();
    setPage(page + 1);

    onUpdateDataSource(tagColors, reqs.results);
  };

  if (loading) {
    return (
      <Card className="w-full p-8">
        <CoreLoading size={1} />
      </Card>
    );
  }

  return (
    <Card className="w-full p-8">
      {contextHolder}
      <CardHeader
        floated={false}
        shadow={false}
        className="rounded-none flex justify-between"
      >
        <Typography variant="h5" color="blue-gray" className="mb-2">
          Requisitos
        </Typography>
      </CardHeader>
      <CardBody className="overflow-scroll px-0">
        <AntTable
          columns={BASE_REQUIREMENTS_TABLE_HEAD}
          dataSource={dataSource}
          pagination={false}
        />
      </CardBody>
      <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
        <Typography variant="small" color="blue-gray" className="font-normal">
          Página {page} de {totalPages}
        </Typography>
        <div className="flex gap-2">
          {!!previous && (
            <AntButton
              type="dashed"
              className="flex items-center gap-3"
              onClick={onFetchPrevious}
            >
              Anterior
            </AntButton>
          )}
          {!!next && (
            <AntButton
              type="primary"
              className="flex items-center gap-3"
              onClick={onFetchNext}
            >
              Siguiente
            </AntButton>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
